/**
 * True / False drill strategy. A True / False step plays a Vocabulary stack:
 * the question is the item's source (or target, per direction), the solve
 * card shows one translation — the item's own or another item's — and the
 * student says whether it is right. The stored answer is the raw emitted
 * `{ shown, verdict }` (like CrossMatch's object answers).
 */
import type { DrillTrueFalse } from 'src/types/components';
import type { Try } from 'src/types/course/step';
import { isTypedAnswerCorrect } from 'src/modules/Step/views/StepView/utilities/typedAnswer';
import { escapeText } from './escapeText';
import { isTargetToSource } from './direction';
import type {
  DrillContext,
  DrillItemMap,
  DrillResultRowMap,
  DrillStrategy,
  TrueFalseAnswer,
} from './types';

type TrueFalseItem = DrillItemMap['TrueFalse'];
type QaSettingsLike = { direction?: unknown } | null | undefined;

const answerKey = (qaSettings: QaSettingsLike) =>
  isTargetToSource(qaSettings) ? 'source' : 'target';
const questionKey = (qaSettings: QaSettingsLike) =>
  isTargetToSource(qaSettings) ? 'target' : 'source';

function correctAnswer(item: TrueFalseItem, ctx: DrillContext): string | null {
  return item?.[answerKey(ctx.drillType?.qa_settings)] ?? null;
}

function isAnswer(value: unknown): value is TrueFalseAnswer {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as TrueFalseAnswer).shown === 'string' &&
    typeof (value as TrueFalseAnswer).verdict === 'boolean'
  );
}

/** '✓ dog' / '✗ cat': the shown translation with the student's verdict. */
export function trueFalseAnswerText(answer: unknown): string | undefined {
  if (!isAnswer(answer)) return typeof answer === 'string' ? answer : undefined;
  return `${answer.verdict ? '✓' : '✗'} ${answer.shown}`;
}

const errorRow: DrillResultRowMap['TrueFalse'] = {
  id: -1,
  source: undefined,
  correct: undefined,
  tries: [],
};

export const trueFalseStrategy: DrillStrategy<'TrueFalse'> = {
  kind: 'TrueFalse',
  speaksQuestion: true,
  speaksAnswer: true,
  errorRow,

  correctAnswer,

  /** Right when the verdict matches whether the shown translation is right. */
  isCorrect(answer, item, ctx) {
    if (!isAnswer(answer)) return false;
    const correct = correctAnswer(item, ctx);
    if (typeof correct !== 'string') return false;
    return (
      answer.verdict ===
      isTypedAnswerCorrect(answer.shown, correct, ctx.typingRules)
    );
  },

  // HTML members are rendered via v-html: text is escaped (BUG-21).
  feedbackAnswerHtml(answer) {
    return escapeText(trueFalseAnswerText(answer) ?? '');
  },

  feedbackCorrectAnswer(item, ctx) {
    return escapeText(correctAnswer(item, ctx));
  },

  feedbackSubtitle(questionText) {
    return escapeText(questionText);
  },

  helpAnswer(item, ctx) {
    return escapeText(correctAnswer(item, ctx));
  },

  /** A confirmed translation is spoken; a rejected one is not. */
  spokenAnswer(answer) {
    return isAnswer(answer) && answer.verdict ? answer.shown : '';
  },

  questionText(item, ctx) {
    return item?.[questionKey(ctx.drillType?.qa_settings)] || '';
  },

  resultRow(item, id, tries, ctx) {
    const qa = ctx.drillType?.qa_settings;
    return {
      id,
      source: item?.[questionKey(qa)],
      correct: item?.[answerKey(qa)],
      tries: tries.map((tryItem: Try) => ({
        duration: tryItem.duration,
        answer: trueFalseAnswerText(tryItem.answer),
        isCorrect: tryItem.isCorrect,
        isHelp: tryItem.isHelp,
        ...(tryItem.isTimeout ? { isTimeout: true } : {}),
      })),
    };
  },
};

/**
 * The translation the solve card shows for the current item: its own answer
 * about half the time, otherwise another item's (one that is not also a right
 * answer). With no such other item the own answer is shown.
 */
export function trueFalseStatement(
  orderedStackSource: readonly TrueFalseItem[],
  currentStackItem: TrueFalseItem | undefined,
  drillType: DrillTrueFalse['attributes'] | undefined,
  random: () => number = Math.random,
): { shown: string; isRight: boolean } | null {
  const key = answerKey(drillType?.qa_settings as QaSettingsLike);
  const correct = currentStackItem?.[key];
  if (!correct) return null;
  if (random() < 0.5) return { shown: correct, isRight: true };

  const others = orderedStackSource
    .map((item) => item?.[key])
    .filter(
      (value): value is string =>
        !!value &&
        !isTypedAnswerCorrect(value, correct, {
          ignorePunctuation: true,
          ignoreCapitalization: true,
          ignoreWordOrder: false,
        }),
    );
  const unique = [...new Set(others)];
  if (!unique.length) return { shown: correct, isRight: true };
  const shown = unique[Math.floor(random() * unique.length)] as string;
  return { shown, isRight: false };
}
